// /admin/users — every account, with the few actions an admin actually needs.
//
// Scope is narrow on purpose: list, search, unlock, suspend. No promote/demote —
// creating another site admin is a big lever and belongs in deliberate SQL, not
// one click from a table. No delete, no resend-verification. Every button here is
// one someone might reach for in a hurry, so the set is kept to the ones that are
// safe to reach for.
//
// The Masquerade action starts READ-ONLY. Write mode is entered from the banner,
// which is a deliberate second step and not a checkbox here — the safe mode has to
// be the one you get by clicking the obvious button.

import {
  ApiClientError,
  adminListUsers,
  adminUpdateUser,
  startMasquerade,
} from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import {
  SiteRole,
  TimeFormat,
  fmtInstant,
  type AdminUserOrder,
  type AdminUserRow,
  type AdminUserStatus,
} from '@msc/shared';
import { viewerZone } from '../viewerZone.js';
import { adminError, adminSubnav, rowActions } from './adminNav.js';
import { invalidateChrome } from '../chrome.js';

const STATUSES: Array<{ value: AdminUserStatus; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'locked', label: 'Locked out' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'pending', label: 'Never verified' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'all', label: 'All' },
];

const ORDERS: Array<{ value: AdminUserOrder; label: string }> = [
  { value: 'name', label: 'Name' },
  { value: 'newest', label: 'Newest' },
  { value: 'lastLogin', label: 'Last login' },
];

export const adminUsersView: View = async (outlet) => {
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  let zone: string;
  try {
    zone = await viewerZone();
  } catch {
    zone = 'UTC';
  }

  let status: AdminUserStatus = 'active';
  let order: AdminUserOrder = 'name';
  let search = '';

  const tableHost = el('div', {}, [el('p', { class: 'muted' }, 'Loading')]);
  const feedback = el('p', { class: 'form-error', hidden: true });

  const searchBox = el('input', {
    id: 'admin-user-search',
    name: 'q',
    type: 'search',
    placeholder: 'Name, email, or phone',
  }) as HTMLInputElement;

  const statusSelect = el(
    'select',
    { id: 'admin-user-status', name: 'status' },
    STATUSES.map((s) => el('option', { value: s.value }, s.label)),
  ) as HTMLSelectElement;

  const orderSelect = el(
    'select',
    { id: 'admin-user-order', name: 'order' },
    ORDERS.map((o) => el('option', { value: o.value }, o.label)),
  ) as HTMLSelectElement;

  async function load(): Promise<void> {
    feedback.hidden = true;
    try {
      const rows = await adminListUsers({ q: search, status, order });
      tableHost.replaceChildren(rows.length ? table(rows) : empty(status));
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        navigate('/login', true);
        return;
      }
      main.replaceChildren(
        el('div', { class: 'page-head' }, [el('h1', {}, 'Admin')]),
        adminSubnav('users'),
        ...adminError(err instanceof ApiClientError ? err.status : undefined),
      );
    }
  }

  async function act(
    row: AdminUserRow,
    changes: { isSuspended?: boolean; unlock?: true },
  ): Promise<void> {
    feedback.hidden = true;
    try {
      await adminUpdateUser(row.userId, changes);
      // Re-load rather than patch the row: a change can move it out of the
      // current filter, and leaving it in place would misreport the filter.
      await load();
    } catch (err) {
      feedback.textContent =
        err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
      feedback.hidden = false;
    }
  }

  function table(rows: AdminUserRow[]): HTMLElement {
    return el('table', { class: 'table' }, [
      el('thead', {}, [
        el('tr', {}, [
          el('th', {}, 'Account'),
          el('th', {}, 'Groups'),
          el('th', {}, 'Last login'),
          el('th', {}, 'Joined'),
          el('th', {}, 'State'),
          el('th', {}, 'Actions'),
        ]),
      ]),
      el(
        'tbody',
        {},
        rows.map((u) =>
          el('tr', {}, [
            el('td', {}, [
              el('strong', {}, u.fullName || el('span', { class: 'muted' }, '(no name)')),
              el('br', {}),
              // Email OR phone: a phone-only signup has no address at all, and
              // showing an empty cell would read as missing data.
              el('span', { class: 'muted small' }, u.email || u.phone || `#${u.userId}`),
            ]),
            el('td', {}, String(u.groupCount)),
            el('td', {}, u.lastLoginDate ? (fmtInstant(u.lastLoginDate, zone, TimeFormat.SQL_DATE) ?? '') : el('span', { class: 'muted' }, 'never')),
            el('td', {}, fmtInstant(u.createdAt, zone, TimeFormat.SQL_DATE) ?? ''),
            el('td', {}, stateBadges(u)),
            el('td', {}, actions(u)),
          ]),
        ),
      ),
    ]);
  }

  /**
   * Every state that is currently true, not one summary word.
   *
   * "Locked" and "Locked out" are shown as separate badges because they are two
   * different mechanisms: the manual hold an admin set, and the automatic
   * three-strikes lockout that expires on its own. An admin who cannot tell them
   * apart cannot tell whether Unlock is even needed.
   */
  function stateBadges(u: AdminUserRow): HTMLElement[] {
    const badges: HTMLElement[] = [];
    if (u.isDeleted) badges.push(el('span', { class: 'badge badge-danger' }, 'Deleted'));
    if (u.isSuspended) badges.push(el('span', { class: 'badge badge-suspended' }, 'Suspended'));
    if (u.isLocked) badges.push(el('span', { class: 'badge badge-danger' }, 'Locked'));
    if (u.autoLockedOut) {
      badges.push(
        el(
          'span',
          {
            class: 'badge badge-warning',
            title: `${u.loginAttempt} failed sign-ins. Clears itself 30 minutes after the last one.`,
          },
          'Locked out',
        ),
      );
    }
    if (!u.isActive && !u.isDeleted) {
      badges.push(el('span', { class: 'badge badge-warning' }, 'Unverified'));
    }
    if (u.siteRole >= SiteRole.ADMIN) {
      badges.push(el('span', { class: 'badge badge-success' }, 'Site admin'));
    }
    if (!badges.length) badges.push(el('span', { class: 'badge badge-success' }, 'Active'));
    return badges;
  }

  function actions(u: AdminUserRow): HTMLElement[] {
    const out: HTMLElement[] = [];

    // Not offered for another site admin (the server refuses it), for a deleted
    // account, or for a suspended one — masquerading into a session that cannot
    // log in would just reproduce the suspension, not the user's problem.
    if (u.siteRole < SiteRole.ADMIN && !u.isDeleted && !u.isSuspended) {
      const masqBtn = el('button', { type: 'button', class: 'btn btn-link' }, 'Masquerade');
      masqBtn.addEventListener('click', () => {
        if (
          !confirm(
            `View the site as ${u.fullName || u.email || `#${u.userId}`}?

` +
              `You start in READ-ONLY mode and a red banner stays on screen until ` +
              `you stop. Emails, texts, and push notifications are suppressed the ` +
              `whole time, and everything you do is logged against your own account.`,
          )
        ) {
          return;
        }
        masqBtn.setAttribute('disabled', 'disabled');
        void startMasquerade(u.userId)
          .then(() => {
            // The nav's cached identity is the admin's; without this the header
            // keeps showing the admin's name next to a banner saying otherwise.
            invalidateChrome();
            navigate('/', true);
          })
          .catch((err: unknown) => {
            feedback.textContent =
              err instanceof ApiClientError ? err.message : 'Could not start.';
            feedback.hidden = false;
            masqBtn.removeAttribute('disabled');
          });
      });
      out.push(masqBtn);
    }

    // Only offered when there is something to clear, so the button is never a
    // no-op an admin has to guess about.
    if (u.isLocked || u.autoLockedOut || u.loginAttempt > 0) {
      const unlockBtn = el('button', { type: 'button', class: 'btn btn-link' }, 'Unlock');
      unlockBtn.addEventListener('click', () => void act(u, { unlock: true }));
      out.push(unlockBtn);
    }

    const suspendBtn = el(
      'button',
      { type: 'button', class: u.isSuspended ? 'btn btn-link' : 'btn btn-link btn-danger' },
      u.isSuspended ? 'Restore' : 'Suspend',
    );
    suspendBtn.addEventListener('click', () => {
      if (
        !u.isSuspended &&
        !confirm(
          `Suspend ${u.fullName || u.email || `#${u.userId}`}?\n\nThey are signed out of ` +
            `every device immediately and cannot log back in. Their ${u.groupCount} group ` +
            `membership(s) are untouched. Reversible.`,
        )
      ) {
        return;
      }
      void act(u, { isSuspended: !u.isSuspended });
    });
    out.push(suspendBtn);

    return rowActions(out);
  }

  const filters = el('form', { method: 'post', class: 'filter-bar', novalidate: true }, [
    el('label', { for: 'admin-user-search' }, 'Search'),
    searchBox,
    el('label', { for: 'admin-user-status' }, 'State'),
    statusSelect,
    el('label', { for: 'admin-user-order' }, 'Order'),
    orderSelect,
  ]) as HTMLFormElement;

  filters.addEventListener('submit', (ev) => ev.preventDefault());

  let debounce: number | undefined;
  searchBox.addEventListener('input', () => {
    search = searchBox.value.trim();
    window.clearTimeout(debounce);
    debounce = window.setTimeout(() => void load(), 250);
  });
  statusSelect.addEventListener('change', () => {
    status = statusSelect.value as AdminUserStatus;
    void load();
  });
  orderSelect.addEventListener('change', () => {
    order = orderSelect.value as AdminUserOrder;
    void load();
  });

  main.replaceChildren(
    el('div', { class: 'page-head' }, [el('h1', {}, 'Admin')]),
    adminSubnav('users'),
    el('h2', {}, 'Users'),
    el(
      'p',
      { class: 'muted small' },
      'Unlock clears both kinds of lockout: the automatic one from repeated bad ' +
        'passwords, and a manual hold. Suspending signs the account out everywhere ' +
        'and refuses further logins. Site roles are deliberately not editable here.',
    ),
    filters,
    feedback,
    tableHost,
  );

  await load();
};

function empty(status: AdminUserStatus): HTMLElement {
  const msg =
    status === 'locked'
      ? 'Nobody is locked out.'
      : status === 'suspended'
        ? 'No suspended accounts.'
        : status === 'pending'
          ? 'Every account has been verified.'
          : status === 'deleted'
            ? 'No deleted accounts.'
            : 'No accounts match.';
  return el('p', { class: 'muted' }, msg);
}
